import {
  Grid,
  Input,
  Stack,
  LoadingOverlay,
  PaperProps,
  Button,
  Group,
  Tooltip,
} from "@mantine/core";
import React from "react";
import { useForm } from "@mantine/form";
import { useSearchParams, useRouter } from "next/navigation";
import { createOrganization } from "@/services/organizations/service";
import { Organization } from "../signup/create-organization/CreateOrganizationInterfaces";
import { z as zod } from "zod";
import { zodResolver } from "@mantine/form";

import useUserOrganizations from "./use-user-organizations";
import classes from "./CreateNewOrganizationForm.module.css";

const schema = zod.object({
  name: zod
    .string()
    .max(50, { message: "Name is too long" })
    .min(3, { message: "Name is too short" })
    .regex(/^[a-zA-Z0-9\s_-]+$/, {
      message: "Only letters, numbers, underscores, and hyphens are allowed",
    }),
  slug: zod
    .string()
    .max(50, { message: "Slug is too long" })
    .min(4, { message: "Slug is too short" })
    .regex(/^[a-z0-9_]+$/, {
      message: "Only lowercase letters, numbers and underscores are allowed.",
    }),
});

interface CreateNewOrganizationFormProps extends PaperProps {
  onSuccess?: () => void;
}

export function CreateNewOrganizationForm({
  onSuccess,
  ...props
}: CreateNewOrganizationFormProps) {
  const router = useRouter();
  const params = useSearchParams();
  const { addAndSelectOrganization } = useUserOrganizations();
  const form = useForm({
    initialValues: {
      createModalOpened: false,
      alertModalOpened: false,
      isLoading: false,
      modalColor: "",
      modalIcon: null,
      modalMessage: "",
      modalTitle: "",
      name: "",
      slug: "",
    },
    validate: zodResolver(schema),
  });

  const allRequiredFields = Boolean(form.values.name && form.values.slug);

  const organizationData: Organization = {
    name: form.values.name,
    slug: form.values.slug,
    send_notifications: true,
  };

  const handleCreateOrganization = async () => {
    form.setFieldValue("isLoading", true);

    form.setErrors({
      name: null,
      slug: null,
      send_notifications: null,
    });

    const formValidation = form.validate();
    if (formValidation.hasErrors) {
      form.setFieldValue("isLoading", false);
      return;
    }

    try {
      const newOrganization = await createOrganization(organizationData);
      form.setFieldValue("isLoading", false);
      addAndSelectOrganization(newOrganization);
      if (onSuccess) onSuccess();

      setTimeout(() => {
        router.push("/batches");
      }, 1000);
    } catch (error: any) {
      form.setFieldValue("isLoading", false);
      if (error.response && error.response.data && error.response.data.detail) {
        const errorMsg = error.response.data.detail;
        form.setErrors({ slug: errorMsg });
      } else {
        const errorMsg = (error as Error).message;
        form.setErrors({ slug: errorMsg });
      }
      console.error(
        `Error creating organization`,
        error.response?.data?.detail || error.message
      );
    }
  };

  return (
    <div>
      <LoadingOverlay
        visible={form.values.isLoading}
        loaderProps={{ type: "bars" }}
        zIndex={1000}
        pos={"fixed"}
      />
      <div
        style={{
          padding: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "10vh",
        }}
      >
        <Grid>
          <Grid.Col>
            <Stack>
              <Input.Wrapper
                label="Name"
                description="The name of your organization. It will be displayed in the platform"
                error={form.errors.name}
              >
                <Input
                  placeholder=""
                  {...form.getInputProps("name")}
                  className={classes.transparentBackground}
                />
              </Input.Wrapper>
              <Input.Wrapper
                label="Key"
                description="This is a unique identifier for your organization"
                error={form.errors.slug}
              >
                <Input
                  placeholder="Use only lowercase letters, numbers, dashes and underscores"
                  {...form.getInputProps("slug")}
                  className={classes.transparentBackground}
                />
              </Input.Wrapper>
            </Stack>
          </Grid.Col>
        </Grid>
      </div>
      <Group
        style={{
          marginTop: 41,
          justifyContent: "center",
        }}
      >
        <Tooltip
          label="You need to fill the name and organization key before you can create your organization."
          disabled={!allRequiredFields}
        >
          <Button
            color="blue"
            onClick={handleCreateOrganization}
            disabled={!allRequiredFields}
          >
            Create
          </Button>
        </Tooltip>
      </Group>
    </div>
  );
}
